import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  TutorContentReviewItemDTO,
  acceptAllContentDrafts,
  acceptContentDraft,
  listContentReview,
  rejectContentDraft,
} from '../../../services/tutorQuizService';

interface Props {
  courseId: number;
}

const PREVIEW_LEN = 200;

function DraftCard({ item, courseId, onAccept, onReject }: {
  item: TutorContentReviewItemDTO;
  courseId: number;
  onAccept: (id: number) => void;
  onReject: (id: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const content = item.draftContent ?? '';
  const preview = content.length > PREVIEW_LEN ? content.slice(0, PREVIEW_LEN) + '…' : content;

  async function handle(fn: () => Promise<void>) {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  }

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" fontWeight={500}>{item.nodeLabel}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
          {expanded ? content : preview}
        </Typography>
        {content.length > PREVIEW_LEN && (
          <Button size="small" onClick={() => setExpanded(e => !e)}>
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
          <Button
            size="small" variant="outlined" color="error"
            disabled={busy}
            onClick={() => handle(() => rejectContentDraft(courseId, item.draftId!)
              .then(() => onReject(item.draftId!)))}
          >
            Reject
          </Button>
          <Button
            size="small" variant="contained"
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : undefined}
            onClick={() => handle(() => acceptContentDraft(courseId, item.draftId!)
              .then(() => onAccept(item.draftId!)))}
          >
            Accept
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function TutorContentReviewQueue({ courseId }: Props) {
  const [items, setItems] = useState<TutorContentReviewItemDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acceptingAll, setAcceptingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listContentReview(courseId));
    } catch {
      setError('Failed to load content review queue.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  function removeItem(draftId: number) {
    setItems(prev => prev.filter(i => i.draftId !== draftId));
  }

  async function handleAcceptAll() {
    setAcceptingAll(true);
    try {
      await acceptAllContentDrafts(courseId);
      setItems([]);
    } catch {
      setError('Accept all failed.');
    } finally {
      setAcceptingAll(false);
    }
  }

  if (loading) return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {items.length > 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            {items.length} draft{items.length !== 1 ? 's' : ''} pending review
          </Typography>
          <Button
            variant="outlined" size="small"
            disabled={acceptingAll}
            startIcon={acceptingAll ? <CircularProgress size={14} /> : undefined}
            onClick={handleAcceptAll}
          >
            Accept All
          </Button>
        </Stack>
      )}

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No content drafts pending review.</Typography>
      ) : (
        <Stack spacing={1}>
          {items.map(item => (
            <DraftCard
              key={item.draftId}
              item={item}
              courseId={courseId}
              onAccept={id => removeItem(id)}
              onReject={id => removeItem(id)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
